import React, { useEffect, useState } from 'react';
import '../../../styles/components/Dashboard.scss';
import '../../../styles/components/SurveyList.scss';
import { supabase } from '../../../helpers/Supabase';

interface Survey {
  id: number;
  store_id: string;
  response: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  created_at: string;
  store_name?: string;
  responses?: any[]; // Add responses field for conversations
}

interface Props {
  onSurveysLoaded?: (surveys: Survey[]) => void; // Add callback prop
}

const SurveyList: React.FC<Props> = ({ onSurveysLoaded }) => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [stores, setStores] = useState<{ id: string; store_name: string }[]>([]);
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [selectedSentiment, setSelectedSentiment] = useState<'positive' | 'neutral' | 'negative' | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [isUpdatingSentiment, setIsUpdatingSentiment] = useState(false);
  const [sentimentSaved, setSentimentSaved] = useState(false);
  const surveysPerPage = 10;

  useEffect(() => {
    const fetchSurveys = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userRows } = await supabase
        .from('users')
        .select('client_id')
        .eq('auth_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const userRow = userRows && userRows.length > 0 ? userRows[0] : null;
      const userError = !userRow ? new Error('User not found') : null;

      console.log("Fetched userRow:", userRow);
      console.log("Expected client_id:", userRow?.client_id);
      if (userError) console.error("User fetch error:", userError);

      if (!userRow) return;

      const { data: storeRows } = await supabase
        .from('stores')
        .select('id, store_name')
        .eq('client_id', userRow.client_id)
        .eq('is_active', true);

      setStores(storeRows || []);

      const { data: surveyRows } = await supabase
        .from('surveys') // ✅ FIXED: was 'feedback'
        .select('id, store_id, responses, sentiment, created_at')
        .eq('client_id', userRow.client_id);

      if (surveyRows && storeRows) {
        const storeMap = Object.fromEntries(storeRows.map((store) => [store.id, store.store_name]));
        const enriched = surveyRows
          .filter((s: any) => {
            const responses = s.responses;
            return (
              Array.isArray(responses) &&
              responses.length > 1 // means more than just greeting
            );
          })
          .map((s: any) => ({
            ...s,
            store_name: storeMap[s.store_id] || 'Unknown',
          }));

        setSurveys(enriched);
        if (onSurveysLoaded) onSurveysLoaded(enriched); // Pass surveys to parent
      }
    };

    fetchSurveys();
  }, [onSurveysLoaded]);

  // Apply all filters
  const filtered = surveys.filter((survey) => {
    // Store filter - handle UUID strings
    if (selectedStore && survey.store_id !== selectedStore) return false;
    
    // Sentiment filter
    if (selectedSentiment && survey.sentiment !== selectedSentiment) return false;
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const surveyDate = new Date(survey.created_at);
      const startDate = dateRange.start ? new Date(dateRange.start) : null;
      const endDate = dateRange.end ? new Date(dateRange.end) : null;
      
      if (startDate && surveyDate < startDate) return false;
      if (endDate && surveyDate > endDate) return false;
    }
    
    return true;
  });

  const paginated = filtered.slice(
    (currentPage - 1) * surveysPerPage,
    currentPage * surveysPerPage
  );

  const totalPages = Math.ceil(filtered.length / surveysPerPage);

  // Get sentiment counts
  const sentimentCounts = {
    positive: surveys.filter(s => s.sentiment === 'positive').length,
    neutral: surveys.filter(s => s.sentiment === 'neutral').length,
    negative: surveys.filter(s => s.sentiment === 'negative').length
  };

  const clearFilters = () => {
    setSelectedStore(null);
    setSelectedSentiment(null);
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedStore || selectedSentiment || dateRange.start || dateRange.end;

  const updateSentiment = async (surveyId: number, newSentiment: 'positive' | 'neutral' | 'negative') => {
    setIsUpdatingSentiment(true);
    try {
      // Find the current survey to get all its data
      const currentSurvey = surveys.find(s => s.id === surveyId);
      if (!currentSurvey) {
        console.error('Survey not found');
        return;
      }

      // Use upsert instead of update to avoid PATCH method
      const { error } = await supabase
        .from('surveys')
        .upsert({
          id: surveyId,
          store_id: currentSurvey.store_id,
          response: currentSurvey.response,
          sentiment: newSentiment,
          created_at: currentSurvey.created_at
        }, {
          onConflict: 'id'
        });

      if (error) {
        console.error('Error updating sentiment:', error);
        alert('Failed to update sentiment. Please try again.');
        return;
      }

      setSurveys(prev => prev.map(s => 
        s.id === surveyId ? { ...s, sentiment: newSentiment } : s
      ));
      if (selectedSurvey && selectedSurvey.id === surveyId) {
        setSelectedSurvey(prev => prev ? { ...prev, sentiment: newSentiment } : null);
      }
      setSentimentSaved(true);
      setTimeout(() => setSentimentSaved(false), 1500);
      console.log('Sentiment updated successfully!');
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setIsUpdatingSentiment(false);
    }
  };

  return (
    <div className="survey-list">
      <div className="survey-header">
        <h2>Survey Responses</h2>
        <div className="survey-stats">
          <span className="stat-item">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{surveys.length}</span>
          </span>
          <span className="stat-item">
            <span className="stat-label">Filtered:</span>
            <span className="stat-value">{filtered.length}</span>
          </span>
        </div>
      </div>

      <div className="filters-container">
        {/* Store Filter */}
        <div className="filter-group">
          <label htmlFor="store-filter" className="filter-label">Store</label>
          <select 
            id="store-filter"
            value={selectedStore ? selectedStore : ''}
            onChange={(e) => {
              const storeId = e.target.value ? e.target.value : null;
              setSelectedStore(storeId);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.store_name}
              </option>
            ))}
          </select>
        </div>

        {/* Sentiment Filter */}
        <div className="filter-group">
          <label className="filter-label">Sentiment</label>
          <div className="sentiment-filters">
            <button
              className={`sentiment-btn ${selectedSentiment === 'positive' ? 'active' : ''}`}
              onClick={() => {
                setSelectedSentiment(selectedSentiment === 'positive' ? null : 'positive');
                setCurrentPage(1);
              }}
            >
              <span className="sentiment-icon positive">😊</span>
              <span className="sentiment-text">Positive</span>
              <span className="sentiment-count">{sentimentCounts.positive}</span>
            </button>
            <button
              className={`sentiment-btn ${selectedSentiment === 'neutral' ? 'active' : ''}`}
              onClick={() => {
                setSelectedSentiment(selectedSentiment === 'neutral' ? null : 'neutral');
                setCurrentPage(1);
              }}
            >
              <span className="sentiment-icon neutral">😐</span>
              <span className="sentiment-text">Neutral</span>
              <span className="sentiment-count">{sentimentCounts.neutral}</span>
            </button>
            <button
              className={`sentiment-btn ${selectedSentiment === 'negative' ? 'active' : ''}`}
              onClick={() => {
                setSelectedSentiment(selectedSentiment === 'negative' ? null : 'negative');
                setCurrentPage(1);
              }}
            >
              <span className="sentiment-icon negative">😞</span>
              <span className="sentiment-text">Negative</span>
              <span className="sentiment-count">{sentimentCounts.negative}</span>
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="filter-group">
          <label className="filter-label">Date Range</label>
          <div className="date-filters">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, start: e.target.value }));
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="Start date"
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, end: e.target.value }));
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="End date"
            />
          </div>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button onClick={clearFilters} className="clear-filters-btn">
            Clear Filters
          </button>
        )}
      </div>

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Store</th>
            <th>Sentiment</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((s) => (
            <tr
              key={s.id}
              className={`sentiment-${s.sentiment}`}
              onClick={() => setSelectedSurvey(s)}
              style={{ cursor: 'pointer' }}
            >
              <td>{new Date(s.created_at).toLocaleDateString()}</td>
              <td>{s.store_name}</td>
              <td>
                <span className={`sentiment-badge ${s.sentiment}`}>
                  {s.sentiment === 'positive' && '😊'}
                  {s.sentiment === 'neutral' && '😐'}
                  {s.sentiment === 'negative' && '😞'}
                  {s.sentiment}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className="no-results">
          <p>No surveys match your current filters.</p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={currentPage === i + 1 ? 'active' : ''}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {selectedSurvey && (
        <div className="survey-modal-overlay" onClick={() => setSelectedSurvey(null)}>
          <div className="survey-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Full Conversation</h3>
              <button className="close-btn" onClick={() => setSelectedSurvey(null)}>×</button>
            </div>

            <div className="chat-history">
              {Array.isArray(selectedSurvey.responses) ? (
                selectedSurvey.responses.flat().map((entry: any, index: number) => (
                  <div key={index} className={`chat-bubble ${entry.role}`}>
                    <p>{entry.content}</p>
                  </div>
                ))
              ) : (
                <div className="chat-bubble assistant">
                  <p>{selectedSurvey.response || 'No content available'}</p>
                </div>
              )}
            </div>
            
            <div className="chat-footer">
              <div className="footer-left">
                <small>
                  Store: <strong>{selectedSurvey.store_name}</strong> • 
                  Current Sentiment: <strong className={`sentiment-${selectedSurvey.sentiment}`}>{selectedSurvey.sentiment}</strong><br />
                  <span>{new Date(selectedSurvey.created_at).toLocaleString()}</span>
                </small>
              </div>
              <div className="footer-right">
                <div className="sentiment-selector-card">
                  <div className="sentiment-selector">
                    <div className="sentiment-options">
                      <button
                        className={`sentiment-option positive ${selectedSurvey.sentiment === 'positive' ? 'active' : ''}`}
                        onClick={() => updateSentiment(selectedSurvey.id, 'positive')}
                        disabled={isUpdatingSentiment}
                      >
                        <span className="sentiment-emoji">😊</span>
                        <span className="sentiment-text">Positive</span>
                        {selectedSurvey.sentiment === 'positive' && <span className="sentiment-sparkle">✨</span>}
                        {isUpdatingSentiment && selectedSurvey.sentiment === 'positive' && <span className="loading-spinner"></span>}
                      </button>
                      <button
                        className={`sentiment-option neutral ${selectedSurvey.sentiment === 'neutral' ? 'active' : ''}`}
                        onClick={() => updateSentiment(selectedSurvey.id, 'neutral')}
                        disabled={isUpdatingSentiment}
                      >
                        <span className="sentiment-emoji">😐</span>
                        <span className="sentiment-text">Neutral</span>
                        {selectedSurvey.sentiment === 'neutral' && <span className="sentiment-sparkle">✨</span>}
                        {isUpdatingSentiment && selectedSurvey.sentiment === 'neutral' && <span className="loading-spinner"></span>}
                      </button>
                      <button
                        className={`sentiment-option negative ${selectedSurvey.sentiment === 'negative' ? 'active' : ''}`}
                        onClick={() => updateSentiment(selectedSurvey.id, 'negative')}
                        disabled={isUpdatingSentiment}
                      >
                        <span className="sentiment-emoji">😞</span>
                        <span className="sentiment-text">Negative</span>
                        {selectedSurvey.sentiment === 'negative' && <span className="sentiment-sparkle">✨</span>}
                        {isUpdatingSentiment && selectedSurvey.sentiment === 'negative' && <span className="loading-spinner"></span>}
                      </button>
                    </div>
                    {sentimentSaved && <div className="sentiment-saved"><span>✔️ Saved!</span></div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyList;
